// Phase 3B.2 — Live Tenant Provisioning Wizard.
// Connects the existing Customer Creation flow to ShelfLife's tenant-drafts
// transaction engine. Default OFF (REACT_APP_LIVE_TENANT_PROVISIONING=false).
// One draft submit + one atomic activate. No per-resource loops.

import { useState } from "react";
import {
  Building2, Store, Tags, UserCheck, FileSpreadsheet, ClipboardCheck,
  Rocket, CheckCircle2, ChevronRight, ChevronLeft, X, Plus, Trash2,
  Loader2, AlertOctagon, Info, Link as LinkIcon, RefreshCw,
} from "lucide-react";
import { platformPost } from "./api/platformClient";

const STEPS = [
  { key: "company",      label: "Customer",    icon: Building2 },
  { key: "stores",       label: "Stores",      icon: Store },
  { key: "departments",  label: "Departments", icon: Tags },
  { key: "first_admin",  label: "First admin", icon: UserCheck },
  { key: "catalog",      label: "Catalog",     icon: FileSpreadsheet },
  { key: "review",       label: "Review",      icon: ClipboardCheck },
  { key: "activate",     label: "Activate",    icon: Rocket },
];
const DEPT_SUGGESTIONS = ["Cheese", "Bakery", "Deli", "Produce", "Dairy", "Grocery", "Front End", "Meat", "Seafood", "Frozen"];

const slugify = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 32);

const initial = () => ({
  company: { name: "", code: "", legalName: "", headquarters: "", plan: "Starter", notes: "" },
  stores: [{ name: "", address: "" }],
  departments: ["Cheese", "Bakery", "Deli", "Produce"],
  customDept: "",
  first_admin: { name: "", email: "", role: "admin", sendInvite: true },
  catalog: { mode: "defer" }, // 'defer' | 'upload-later'
});

const codeOk = (c) => /^[a-z][a-z0-9-]{1,31}$/.test(c);
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// Per-wizard-instance idempotency key. Generated once at mount and reused
// across retries so the upstream tenant-drafts engine can dedupe replays.
const makeIdempotencyKey = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  // Fallback for environments without crypto.randomUUID
  return `cc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const LiveTenantProvisioningWizard = ({ onClose, onTenantCreated }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState(initial);
  // Transaction state machine: draft | submitting_draft | draft_ok |
  // activating | success | failure | retrying
  const [txState, setTxState] = useState("draft");
  const [draftId, setDraftId] = useState(null);
  const [idempotencyKey] = useState(() => makeIdempotencyKey());
  const [serverIdempotencyKey, setServerIdempotencyKey] = useState(null);
  const [activation, setActivation] = useState(null); // success payload
  const [failure, setFailure] = useState(null);       // { kind, message, status }
  const [retryCount, setRetryCount] = useState(0);

  const step = STEPS[stepIdx];
  const StepIcon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  const setCompany = (patch) => setData((d) => ({ ...d, company: { ...d.company, ...patch } }));
  const setStore = (i, patch) =>
    setData((d) => ({ ...d, stores: d.stores.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addStore = () => setData((d) => ({ ...d, stores: [...d.stores, { name: "", address: "" }] }));
  const removeStore = (i) =>
    setData((d) => ({ ...d, stores: d.stores.length > 1 ? d.stores.filter((_, idx) => idx !== i) : d.stores }));
  const toggleDept = (name) => setData((d) => ({
    ...d,
    departments: d.departments.includes(name)
      ? d.departments.filter((x) => x !== name)
      : [...d.departments, name],
  }));
  const setAdmin = (patch) => setData((d) => ({ ...d, first_admin: { ...d.first_admin, ...patch } }));

  const stepValid = () => {
    if (step.key === "company")
      return data.company.name.trim().length >= 2 && codeOk(data.company.code);
    if (step.key === "stores")
      return data.stores.length >= 1 && data.stores.every((s) => s.name.trim().length >= 2);
    if (step.key === "departments") return data.departments.length >= 1;
    if (step.key === "first_admin")
      return emailOk(data.first_admin.email) && data.first_admin.role !== "platform_superadmin";
    return true;
  };

  const next = () => { if (stepValid() && !isLast) setStepIdx((i) => i + 1); };
  const prev = () => { if (stepIdx > 0 && txState === "draft") setStepIdx((i) => i - 1); };

  const buildDraftPayload = () => ({
    idempotency_key: idempotencyKey,
    company: {
      code: data.company.code,
      name: data.company.name,
      legal_name: data.company.legalName || data.company.name,
      headquarters: data.company.headquarters || null,
      plan: data.company.plan || null,
      notes: data.company.notes || null,
    },
    stores: data.stores
      .filter((s) => s.name.trim())
      .map((s) => ({ name: s.name.trim(), address: s.address || null })),
    departments: data.departments.map((name) => ({ name })),
    first_admin: {
      email: data.first_admin.email,
      name: data.first_admin.name || null,
      role: data.first_admin.role,
      send_invite: data.first_admin.sendInvite,
    },
    catalog: null,
  });

  const activate = async (isRetry = false) => {
    setFailure(null);
    if (isRetry) setRetryCount((n) => n + 1);
    try {
      let id = draftId;
      // 1) Create draft (skip if already created on a previous attempt)
      if (!id) {
        setTxState("submitting_draft");
        const draftResp = await platformPost(
          "/api/platform/tenant-drafts",
          buildDraftPayload()
        );
        // ShelfLife envelope: { ok, draft: { id, idempotency_key, provisioning_state }, replayed }
        // Tolerate two earlier shapes so older upstream builds don't break: draftResp.draft_id at
        // top level, and draftResp.draft.draft_id. New canonical path is draftResp.draft.id.
        const draft = (draftResp && draftResp.draft) || null;
        id =
          (draft && (draft.id || draft.draft_id)) ||
          (draftResp && draftResp.draft_id) ||
          null;
        if (!id) throw Object.assign(new Error("Draft response missing draft_id"), { kind: "error" });
        // Preserve the upstream-issued idempotency key on the draft so subsequent
        // operator-visible state (replay panel, audit row) can show what ShelfLife recorded.
        if (draft && draft.idempotency_key) setServerIdempotencyKey(draft.idempotency_key);
        setDraftId(id);
        setTxState("draft_ok");
      }
      // 2) Activate
      setTxState(isRetry ? "retrying" : "activating");
      // ShelfLife requires the idempotency_key on activate so replays of this
      // operator's click resolve to the same tenant (no duplicates). Prefer the
      // server-echoed value from the draft response; fall back to the
      // wizard-generated key (identical bytes — the server just echoed what we
      // sent on draft create).
      const activateKey = serverIdempotencyKey || idempotencyKey;
      const actResp = await platformPost(
        `/api/platform/tenant-drafts/${id}/activate`,
        { idempotency_key: activateKey, confirm: true }
      );
      if (!actResp || actResp.ok === false) {
        const err = new Error(
          (actResp && actResp.error) || "Activation reported failure"
        );
        err.kind = "activation_failed";
        err.payload = actResp;
        throw err;
      }
      setActivation(actResp);
      setTxState("success");
      if (onTenantCreated && actResp.company_id) onTenantCreated(actResp.company_id);
    } catch (err) {
      setFailure({
        kind: err.kind || "error",
        message: err.message || "Unexpected error",
        status: err.status,
        payload: err.payload,
      });
      setTxState("failure");
    }
  };

  return (
    <div
      data-testid="live-tenant-provisioning-wizard"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <Header onClose={onClose} txState={txState} />
        <Progress stepIdx={stepIdx} txState={txState} />

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {txState === "success" && (
            <SuccessScreen activation={activation} draftData={data} draftId={draftId} idempotencyKey={serverIdempotencyKey || idempotencyKey} onOpen={onTenantCreated} onClose={onClose} />
          )}
          {txState !== "success" && (txState === "failure" || txState === "submitting_draft" || txState === "draft_ok" || txState === "activating" || txState === "retrying") && (
            <TxStatusPanel txState={txState} failure={failure} retryCount={retryCount} />
          )}
          {txState === "draft" && (
            <div data-testid={`wizard-step-${step.key}`}>
              <div className="flex items-center gap-2 mb-4">
                <StepIcon className="h-4 w-4 text-[#0d3b66]" />
                <h2 className="text-lg font-semibold text-slate-900">{step.label}</h2>
              </div>
              {step.key === "company"      && <CompanyStep data={data.company} setCompany={setCompany} />}
              {step.key === "stores"       && <StoresStep stores={data.stores} setStore={setStore} addStore={addStore} removeStore={removeStore} />}
              {step.key === "departments"  && <DepartmentsStep selected={data.departments} toggle={toggleDept} custom={data.customDept} setCustom={(v) => setData((d) => ({ ...d, customDept: v }))} addCustom={() => { if (data.customDept.trim()) { toggleDept(data.customDept.trim()); setData((d) => ({ ...d, customDept: "" })); } }} />}
              {step.key === "first_admin"  && <FirstAdminStep admin={data.first_admin} setAdmin={setAdmin} />}
              {step.key === "catalog"      && <CatalogStep />}
              {step.key === "review"       && <ReviewStep data={data} />}
              {step.key === "activate"     && <ActivateStep data={data} onActivate={() => activate(false)} />}
            </div>
          )}
        </div>

        <Footer
          stepIdx={stepIdx} stepValid={stepValid()} isLast={isLast}
          txState={txState} prev={prev} next={next}
          onActivate={() => activate(false)} onRetry={() => activate(true)} onClose={onClose}
        />
      </div>
    </div>
  );
};

// ===== Sub-components =====
const Header = ({ onClose, txState }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#f4d35e] font-bold bg-[#0d3b66] px-2 py-0.5 rounded inline-block">Live · Phase 3B</div>
      <h1 className="text-xl font-semibold text-slate-900 mt-1">New customer onboarding</h1>
    </div>
    <button data-testid="wizard-close" onClick={onClose} disabled={txState === "activating" || txState === "submitting_draft" || txState === "retrying"} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
      <X className="h-5 w-5" />
    </button>
  </div>
);

const Progress = ({ stepIdx, txState }) => (
  <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2 overflow-x-auto">
    {STEPS.map((s, i) => {
      const active = i === stepIdx && txState === "draft";
      const done = i < stepIdx || txState === "success";
      return (
        <div key={s.key} className="flex items-center gap-2 shrink-0">
          <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${done ? "bg-emerald-500 text-white" : active ? "bg-[#0d3b66] text-white" : "bg-slate-200 text-slate-500"}`}>{done ? "✓" : i + 1}</span>
          <span className={`text-xs ${active ? "font-semibold text-[#0d3b66]" : "text-slate-500"}`}>{s.label}</span>
          {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
        </div>
      );
    })}
  </div>
);

const CompanyStep = ({ data, setCompany }) => (
  <div className="space-y-4">
    <Field label="Company name" required>
      <input data-testid="field-company-name" className={inputCls} value={data.name} onChange={(e) => { setCompany({ name: e.target.value }); if (!data.code) setCompany({ code: slugify(e.target.value) }); }} placeholder="Joe's Market" />
    </Field>
    <Field label="Company code" required hint="Lowercase, kebab-case, 2–32 chars. Used as the tenant handle.">
      <input data-testid="field-company-code" className={inputCls} value={data.code} onChange={(e) => setCompany({ code: e.target.value.toLowerCase() })} placeholder="joes" />
      {data.code && !codeOk(data.code) && <p className="text-xs text-rose-600 mt-1">Code must match ^[a-z][a-z0-9-]{"{1,31}"}$</p>}
    </Field>
    <Field label="Legal name"><input className={inputCls} value={data.legalName} onChange={(e) => setCompany({ legalName: e.target.value })} placeholder="Joe's Market LLC" /></Field>
    <Field label="Headquarters"><input className={inputCls} value={data.headquarters} onChange={(e) => setCompany({ headquarters: e.target.value })} placeholder="Huntington, NY" /></Field>
    <Field label="Plan">
      <select className={inputCls} value={data.plan} onChange={(e) => setCompany({ plan: e.target.value })}>
        <option>Starter</option><option>Growth</option><option>Enterprise</option>
      </select>
    </Field>
    <Field label="Notes"><textarea className={inputCls} rows={3} value={data.notes} onChange={(e) => setCompany({ notes: e.target.value })} placeholder="Internal notes (optional)" /></Field>
  </div>
);

const StoresStep = ({ stores, setStore, addStore, removeStore }) => (
  <div className="space-y-3">
    {stores.map((s, i) => (
      <div key={i} className="rounded-lg border border-slate-200 p-3 grid grid-cols-12 gap-2">
        <input data-testid={`field-store-name-${i}`} className={`${inputCls} col-span-5`} placeholder="Store name" value={s.name} onChange={(e) => setStore(i, { name: e.target.value })} />
        <input className={`${inputCls} col-span-6`} placeholder="Address (optional)" value={s.address} onChange={(e) => setStore(i, { address: e.target.value })} />
        <button onClick={() => removeStore(i)} className="col-span-1 text-rose-400 hover:text-rose-600 disabled:opacity-30" disabled={stores.length === 1}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button data-testid="add-store" onClick={addStore} className="text-sm text-[#0d3b66] hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add another store</button>
  </div>
);

const DepartmentsStep = ({ selected, toggle, custom, setCustom, addCustom }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap gap-2">
      {DEPT_SUGGESTIONS.map((d) => (
        <button key={d} data-testid={`dept-${d}`} onClick={() => toggle(d)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected.includes(d) ? "bg-[#0d3b66] text-white border-[#0d3b66]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>{d}</button>
      ))}
    </div>
    {selected.filter((d) => !DEPT_SUGGESTIONS.includes(d)).length > 0 && (
      <div className="pt-2"><p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Custom</p><div className="flex flex-wrap gap-2">{selected.filter((d) => !DEPT_SUGGESTIONS.includes(d)).map((d) => (<button key={d} onClick={() => toggle(d)} className="px-3 py-1.5 rounded-full text-sm bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100">{d} ✕</button>))}</div></div>
    )}
    <div className="flex gap-2 pt-2 border-t border-slate-200">
      <input className={`${inputCls} flex-1`} placeholder="Add a custom department…" value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} />
      <button onClick={addCustom} className="px-3 rounded-md bg-slate-100 hover:bg-slate-200 text-sm font-medium">Add</button>
    </div>
  </div>
);

const FirstAdminStep = ({ admin, setAdmin }) => (
  <div className="space-y-4">
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2"><Info className="h-3.5 w-3.5 mt-0.5" />This person becomes the <strong>first tenant admin</strong>. They will receive an invite and operate the tenant inside ShelfLife. The platform_superadmin role is intentionally not selectable.</div>
    <Field label="Full name"><input data-testid="field-admin-name" className={inputCls} value={admin.name} onChange={(e) => setAdmin({ name: e.target.value })} placeholder="Joe Owner" /></Field>
    <Field label="Email" required><input data-testid="field-admin-email" className={inputCls} value={admin.email} onChange={(e) => setAdmin({ email: e.target.value })} placeholder="owner@joesmarket.com" /></Field>
    <Field label="Role" required>
      <select data-testid="field-admin-role" className={inputCls} value={admin.role} onChange={(e) => setAdmin({ role: e.target.value })}>
        <option value="admin">Tenant admin (Owner)</option>
        <option value="manager">Store manager</option>
      </select>
    </Field>
    <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={admin.sendInvite} onChange={(e) => setAdmin({ sendInvite: e.target.checked })} />Send invite email on activation</label>
  </div>
);

const CatalogStep = () => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
    <p className="font-medium text-slate-900 mb-2">Catalog import is deferred.</p>
    <p>Customer #2 can import their product catalog from inside ShelfLife after activation. Per ADR-001, catalog management lives on the ShelfLife side after handoff.</p>
  </div>
);

const ReviewStep = ({ data }) => (
  <div className="space-y-4 text-sm">
    <Block title="Customer">
      <Row label="Name" v={data.company.name} /><Row label="Code" v={data.company.code} /><Row label="Plan" v={data.company.plan} /><Row label="HQ" v={data.company.headquarters || "—"} />
    </Block>
    <Block title={`Stores (${data.stores.filter((s) => s.name).length})`}>
      {data.stores.filter((s) => s.name).map((s, i) => (<Row key={i} label={s.name} v={s.address || "—"} />))}
    </Block>
    <Block title={`Departments (${data.departments.length})`}>
      <p className="text-slate-700">{data.departments.join(", ")}</p>
    </Block>
    <Block title="First admin">
      <Row label="Email" v={data.first_admin.email} /><Row label="Role" v={data.first_admin.role} /><Row label="Invite" v={data.first_admin.sendInvite ? "On activation" : "No invite"} />
    </Block>
  </div>
);

const ActivateStep = ({ data, onActivate }) => (
  <div className="text-center py-8">
    <Rocket className="h-12 w-12 text-[#0d3b66] mx-auto mb-3" />
    <h3 className="text-xl font-semibold text-slate-900">Activate {data.company.name}?</h3>
    <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">This will create the tenant, {data.stores.filter((s)=>s.name).length} stores, {data.departments.length} departments, and the first admin in ShelfLife. Atomic.</p>
    <button data-testid="wizard-activate" onClick={onActivate} className="mt-6 inline-flex items-center gap-2 bg-[#0d3b66] hover:bg-[#0a2e52] text-white font-semibold px-6 py-2.5 rounded-lg"><Rocket className="h-4 w-4" /> Activate tenant</button>
  </div>
);

const TxStatusPanel = ({ txState, failure, retryCount }) => {
  if (txState === "failure") {
    const detail = failure?.message || "Unknown error";
    const conflict = failure?.kind === "conflict";
    return (
      <div data-testid="tx-failure" className="text-center py-10">
        <AlertOctagon className="h-12 w-12 text-rose-500 mx-auto mb-3" />
        <h3 className="text-xl font-semibold text-slate-900">Activation failed</h3>
        <p className="text-sm text-slate-600 mt-2 max-w-lg mx-auto">{detail}</p>
        {conflict && <p className="text-xs text-amber-700 mt-2">Conflict (HTTP {failure.status}). Adjust the duplicate field and retry.</p>}
        {retryCount > 0 && <p className="text-xs text-slate-500 mt-2">Retries attempted: {retryCount}</p>}
      </div>
    );
  }
  const labels = {
    submitting_draft: "Saving draft to ShelfLife…",
    draft_ok:         "Draft saved.",
    activating:       "Activating tenant…",
    retrying:         `Retrying activation (attempt ${retryCount + 1})…`,
  };
  return (
    <div data-testid={`tx-${txState}`} className="text-center py-12">
      <Loader2 className="h-10 w-10 text-[#0d3b66] mx-auto mb-3 animate-spin" />
      <p className="text-sm text-slate-700 font-medium">{labels[txState]}</p>
      {(txState === "activating" || txState === "retrying") && (
        <p className="text-xs text-slate-500 mt-1">ShelfLife is creating the company, stores, departments and first admin atomically.</p>
      )}
    </div>
  );
};

const SuccessScreen = ({ activation, draftData, draftId, idempotencyKey, onOpen, onClose }) => {
  const cid = activation.company_id;
  const summary = activation.summary || {};
  const inviteSent = activation.invite_sent !== false;
  return (
    <div data-testid="tx-success" className="py-6">
      <div className="text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-2xl font-semibold text-slate-900">{draftData.company.name} is live.</h3>
        <p className="text-sm text-slate-500 mt-1 font-mono" data-testid="success-company-id">{cid}</p>
        {(draftId || idempotencyKey) && (
          <p className="text-[11px] text-slate-400 mt-1 font-mono" data-testid="success-draft-meta">
            {draftId && <>draft {draftId}</>}{draftId && idempotencyKey && " · "}{idempotencyKey && <>idem {idempotencyKey}</>}
          </p>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6">
        <SuccTile label="Stores" v={summary.stores_created ?? draftData.stores.filter((s)=>s.name).length} />
        <SuccTile label="Departments" v={summary.departments_seeded ?? draftData.departments.length} />
        <SuccTile label="First admin" v={draftData.first_admin.email} small />
      </div>
      {!inviteSent && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="invite-fallback">
          <Info className="h-4 w-4 mt-0.5" />
          <div>
            <strong>Invite email was not sent.</strong> Deliver credentials to <span className="font-mono">{draftData.first_admin.email}</span> out of band, or resend from the user&apos;s row inside ShelfLife.
          </div>
        </div>
      )}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <a className="block rounded-lg bg-[#0d3b66] hover:bg-[#0a2e52] text-white font-semibold px-4 py-3 text-center" data-testid="open-tenant" onClick={() => onOpen && onOpen(cid)} href="#">Open tenant detail</a>
        <button className="rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-3" data-testid="onboard-another" onClick={() => window.location.reload()}>Onboard another</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500 justify-center">
        <span className="inline-flex items-center gap-1"><LinkIcon className="h-3 w-3" />Users tab · Stores tab · Action history</span>
      </div>
      <div className="text-center mt-4">
        <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-700">Close</button>
      </div>
    </div>
  );
};

const Footer = ({ stepIdx, stepValid, isLast, txState, prev, next, onActivate, onRetry, onClose }) => {
  if (txState === "success") return null;
  if (txState === "failure") {
    return (
      <div className="border-t border-slate-200 px-6 py-3 flex justify-between bg-slate-50">
        <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Close</button>
        <button data-testid="wizard-retry" onClick={onRetry} className="inline-flex items-center gap-1.5 bg-[#0d3b66] hover:bg-[#0a2e52] text-white font-semibold px-4 py-2 rounded-lg text-sm"><RefreshCw className="h-3.5 w-3.5" /> Retry activation</button>
      </div>
    );
  }
  if (["submitting_draft", "draft_ok", "activating", "retrying"].includes(txState)) return null;
  return (
    <div className="border-t border-slate-200 px-6 py-3 flex justify-between bg-slate-50">
      <button data-testid="wizard-back" disabled={stepIdx === 0} onClick={prev} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /> Back</button>
      {!isLast ? (
        <button data-testid="wizard-next" disabled={!stepValid} onClick={next} className="inline-flex items-center gap-1.5 bg-[#0d3b66] hover:bg-[#0a2e52] text-white font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-40">Continue <ChevronRight className="h-3.5 w-3.5" /></button>
      ) : (
        <button data-testid="wizard-activate-footer" onClick={onActivate} className="inline-flex items-center gap-1.5 bg-[#0d3b66] hover:bg-[#0a2e52] text-white font-semibold px-4 py-2 rounded-lg text-sm"><Rocket className="h-3.5 w-3.5" /> Activate</button>
      )}
    </div>
  );
};

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d3b66]/30 focus:border-[#0d3b66]";
const Field = ({ label, required, hint, children }) => (
  <label className="block"><span className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</span>{children}{hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}</label>
);
const Block = ({ title, children }) => (<div className="rounded-lg border border-slate-200 p-3"><div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">{title}</div>{children}</div>);
const Row = ({ label, v }) => (<div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">{label}</span><span className="text-slate-900 font-medium text-right">{v}</span></div>);
const SuccTile = ({ label, v, small }) => (<div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-emerald-700">{label}</div><div className={`${small ? "text-xs" : "text-lg font-semibold"} text-emerald-900 mt-0.5 break-all`}>{v}</div></div>);
